/**
 * The open-folder card: header, Files / Folders tabs, nested folder tiles and
 * the paginated document list.
 *
 * Moved out of FolderView.tsx (CR-7754) to keep it under the 500-line cap.
 */

import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/pagination';
import {
  Folder,
  FolderPlus,
  ArrowLeft,
  Building2,
  Info,
  ShieldCheck,
} from 'lucide-react';
import { Document, CategoryWithDocuments } from '../../lib/api';
import { getCategoryColor } from './utils';
import { FolderTile } from './FolderTile';
import { DocumentRowList, DocumentSortHeader, useDocumentSort } from './DocumentRows';
import { DocumentTile, SubLayout, SupplierRow, SupplierTile } from './FolderDocumentTiles';

// Client-side pagination page-size for the drilled-in doc list.
const DOCS_PER_PAGE = 24;


export interface FolderActionSet {
  onDetails?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  onCreateSubfolder?: () => void;
}

interface CategoryCardProps {
  category: CategoryWithDocuments;
  // Direct child folders of this category, rendered as tiles above the
  // document list.
  childFolders: CategoryWithDocuments[];
  // Navigate into a child folder (pushes onto the breadcrumb path).
  onOpenFolder: (id: number | 'uncategorized') => void;
  // Resolves the details / rename / delete / create-subfolder handlers for a
  // folder, each present only when the user is entitled to that action.
  folderActions: (category: CategoryWithDocuments) => FolderActionSet;
  onDownload: (doc: Document) => void;
  onDelete?: (doc: Document) => void;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
  selectedDocIds?: number[];
  onToggleDocSelected?: (id: number) => void;
  onSelectAllDocs?: (ids: number[]) => void;
  onClearSelectedDocs?: () => void;
  onOpenBulkPerms?: () => void;
}

export function CategoryCard({
  category,
  childFolders,
  onOpenFolder,
  folderActions,
  onDownload,
  onDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  subLayout,
  selectedDocIds,
  onToggleDocSelected,
  onSelectAllDocs,
  onClearSelectedDocs,
  onOpenBulkPerms,
}: CategoryCardProps) {
  // Auto-subgrouping is disabled: a folder with many documents used to split
  // into per-uploader "sub-folder" tiles, which read as subfolders being
  // created automatically after a multi-file upload. Always show the flat
  // (paginated) document list instead — folders are only ever created
  // explicitly via "New subfolder".
  const shouldSubgroup = false;

  // Group docs by uploading company_name. Supplier viewers get "Shared"
  // for any cross-supplier doc (the backend scrubs the name before it
  // gets here), so those docs all fall into one safe bucket.
  const subgroups = useMemo(() => {
    if (!shouldSubgroup) return [] as Array<[string, Document[]]>;
    const m: Record<string, Document[]> = {};
    for (const d of category.documents) {
      const key = d.company_name || '(Unassigned)';
      (m[key] = m[key] || []).push(d);
    }
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [category.documents, shouldSubgroup]);

  const [drillInto, setDrillInto] = useState<string | null>(null);
  const drillDocs = useMemo(
    () => (drillInto ? (subgroups.find(([n]) => n === drillInto)?.[1] ?? []) : []),
    [drillInto, subgroups],
  );

  // Pagination state for the currently-visible doc list (drill target if
  // sub-grouped, otherwise the flat category doc list).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DOCS_PER_PAGE);

  const visibleDocs = useMemo(
    () => (shouldSubgroup && !drillInto ? [] : (shouldSubgroup ? drillDocs : category.documents)),
    [shouldSubgroup, drillInto, drillDocs, category.documents],
  );
  // Sort controls for the document list (shared with the supplier Documents
  // tab): clickable Name / Date Uploaded headers; sorting runs over the full
  // visible set before pagination so it orders the whole folder, not one page.
  const { sortKey, sortDir, toggleSort, sorted: sortedDocs } = useDocumentSort(visibleDocs, () => setPage(1));
  const totalDocs = sortedDocs.length;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedDocs = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedDocs.slice(start, start + pageSize);
  }, [sortedDocs, safePage, pageSize]);

  const resetPaging = () => { setPage(1); };

  // "Select all" header checkbox state — covers every document in the folder
  // (all pages), so a bulk permission change can target the whole folder.
  const selectionEnabled = !!onToggleDocSelected;
  const folderDocIds = useMemo(() => sortedDocs.map((d) => d.id), [sortedDocs]);
  const selectedCount = useMemo(
    () => folderDocIds.filter((id) => selectedDocIds?.includes(id)).length,
    [folderDocIds, selectedDocIds],
  );
  const allSelected = folderDocIds.length > 0 && selectedCount === folderDocIds.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const toggleSelectAll = () => {
    if (allSelected) onClearSelectedDocs?.();
    else onSelectAllDocs?.(folderDocIds);
  };

  // When a folder holds BOTH subfolders and files, split them across two
  // tabs (Files | Folders) so neither buries the other. A folder with only
  // one of the two shows that content directly, no tabs.
  const [activeTab, setActiveTab] = useState<'files' | 'folders'>('files');
  const hasSubfolders = !drillInto && childFolders.length > 0;
  const hasFiles = sortedDocs.length > 0;
  const showTabs = hasSubfolders && hasFiles;
  const showSubfolders = hasSubfolders && (!showTabs || activeTab === 'folders');
  const showFiles = (showTabs && activeTab === 'files') || (!showTabs && !hasSubfolders);

  const pager = (!shouldSubgroup || drillInto) && totalPages > 1 ? (
    <Pagination
      currentPage={safePage}
      totalPages={totalPages}
      totalCount={totalDocs}
      itemsPerPage={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      itemLabel="documents"
    />
  ) : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {shouldSubgroup && drillInto && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => { setDrillInto(null); resetPaging(); }}
                aria-label="Back to folders"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div className={`p-2 rounded-lg ${getCategoryColor(category.color)}`}>
              {drillInto ? <Building2 className="h-5 w-5" /> : <Folder className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <CardTitle className="text-xl truncate">
                {drillInto ? drillInto : category.name}
              </CardTitle>
              {!drillInto && category.description && (
                <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
              )}
              {drillInto && (
                <p className="text-sm text-muted-foreground mt-1">
                  {category.name} › {drillDocs.length} {drillDocs.length === 1 ? 'document' : 'documents'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* CR-7754: the open folder's own Details, without going back up a level. */}
            {!drillInto && folderActions(category).onDetails && (
              <Button
                variant="outline"
                size="sm"
                onClick={folderActions(category).onDetails}
              >
                <Info className="h-4 w-4 mr-1.5" />
                Details
              </Button>
            )}
            {!drillInto && folderActions(category).onCreateSubfolder && (
              <Button
                variant="outline"
                size="sm"
                onClick={folderActions(category).onCreateSubfolder}
              >
                <FolderPlus className="h-4 w-4 mr-1.5" />
                New subfolder
              </Button>
            )}
            {shouldSubgroup && !drillInto && (
              <Badge variant="outline" className="text-xs">
                {subgroups.length} {subgroups.length === 1 ? 'folder' : 'folders'}
              </Badge>
            )}
            <Badge variant="secondary">
              {drillInto
                ? `${drillDocs.length} ${drillDocs.length === 1 ? 'document' : 'documents'}`
                : `${category.document_count} ${category.document_count === 1 ? 'document' : 'documents'}`}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Files / Folders tabs — only when the folder holds both. */}
        {showTabs && (
          <div className="flex items-center gap-1 border-b">
            <button
              type="button"
              onClick={() => setActiveTab('files')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'files'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Files ({sortedDocs.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('folders')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === 'folders'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Folders ({childFolders.length})
            </button>
          </div>
        )}
        {/* Nested subfolders — shown when there are no files (no tabs), or when
            the Folders tab is active. Hidden in the uploader-subgroup view. */}
        {showSubfolders && (
          <div className="space-y-2">
            {!showTabs && (
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Subfolders
              </p>
            )}
            {/* Honour the tile/list toggle for nested subfolders too — the
                drilled-in view previously hard-coded a tile grid, so the
                List layout button did nothing once inside a folder. */}
            {subLayout === 'list' ? (
              <div className="divide-y rounded-md border bg-card">
                {childFolders.map((child) => (
                  <FolderTile
                    key={child.id ?? 'uncategorized'}
                    category={child}
                    variant="row"
                    onOpen={() => onOpenFolder(child.id ?? 'uncategorized')}
                    {...folderActions(child)}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {childFolders.map((child) => (
                  <FolderTile
                    key={child.id ?? 'uncategorized'}
                    category={child}
                    onOpen={() => onOpenFolder(child.id ?? 'uncategorized')}
                    {...folderActions(child)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        {showFiles && (shouldSubgroup && !drillInto ? (
          subLayout === 'tiles' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {subgroups.map(([label, docs]) => (
                <SupplierTile
                  key={label}
                  label={label}
                  count={docs.length}
                  onOpen={() => { setDrillInto(label); resetPaging(); }}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {subgroups.map(([label, docs]) => (
                <SupplierRow
                  key={label}
                  label={label}
                  documents={docs}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onOpenDetail={onOpenDetail}
                  docLayout={subLayout}
                />
              ))}
            </div>
          )
        ) : (
          <>
            {/* Multi-select action bar — only when at least one doc is ticked.
                Lets the user set permissions on the whole selection at once. */}
            {onToggleDocSelected && (selectedDocIds?.length ?? 0) > 0 && (
              <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg border bg-primary/5">
                <span className="text-sm font-medium">
                  {selectedDocIds?.length} selected
                </span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => onClearSelectedDocs?.()}>
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => onOpenBulkPerms?.()}>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Set permissions
                  </Button>
                </div>
              </div>
            )}
            {pager}
            {subLayout === 'list' ? (
              <div className="space-y-1">
                <DocumentSortHeader
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  selectable={selectionEnabled}
                  allSelected={allSelected}
                  someSelected={someSelected}
                  onToggleSelectAll={toggleSelectAll}
                />
                <DocumentRowList
                  documents={pagedDocs}
                  onDownload={onDownload}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onPreview={onPreview}
                  onOpenDetail={onOpenDetail}
                  selectedDocIds={selectedDocIds}
                  onToggleDocSelected={onToggleDocSelected}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pagedDocs.map((doc) => (
                  <DocumentTile
                    key={doc.id}
                    doc={doc}
                    onDownload={onDownload}
                    onDelete={onDelete}
                    onEdit={onEdit}
                    onPreview={onPreview}
                    onOpenDetail={onOpenDetail}
                  />
                ))}
              </div>
            )}
            {pager}
          </>
        ))}
      </CardContent>
    </Card>
  );
}
